import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/Button/Button';
import { blogService } from '../../services/blogService';
import { BlogPost } from '../../types/blog';
import ReactMarkdown from 'react-markdown';
import * as S from './styles';

interface BlogIframeEmbed {
  src: string;
  title: string;
  allow: string;
}

type BlogContentPart =
  | { type: 'markdown'; value: string }
  | { type: 'iframe'; value: BlogIframeEmbed };

const iframePattern = /<iframe\b[\s\S]*?<\/iframe>/gi;
const iframeAttributePattern = /([a-zA-Z:-]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g;
const defaultIframeAllow =
  'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
const siteUrl = 'https://austindavenport.com';
const buildingVidIqTopImageUrl =
  'https://64.media.tumblr.com/b9cb3f62418a53c348cd0b6c94167594/c67fe3c718b47b53-02/s1280x1920/c96e10ff077c04eb4c5dab7ddf48c73c86f24653.pnj';

const setMeta = (selector: string, attribute: 'content' | 'href', value: string) => {
  const element = document.querySelector<HTMLMetaElement | HTMLLinkElement>(selector);
  if (element) {
    element.setAttribute(attribute, value);
  }
};

const normalizeAllowedIframeSrc = (src: string): string | null => {
  try {
    const url = new URL(src);
    const hostname = url.hostname.replace(/^www\./, '');

    if (url.protocol !== 'https:') {
      return null;
    }

    if (
      (hostname === 'youtube.com' || hostname === 'youtube-nocookie.com') &&
      url.pathname.startsWith('/embed/')
    ) {
      return url.toString();
    }

    if (hostname === 'player.vimeo.com' && url.pathname.startsWith('/video/')) {
      return url.toString();
    }
  } catch {
    return null;
  }

  return null;
};

const parseIframeEmbed = (html: string): BlogIframeEmbed | null => {
  const attributes = new Map<string, string>();
  let match: RegExpExecArray | null;

  iframeAttributePattern.lastIndex = 0;

  while ((match = iframeAttributePattern.exec(html)) !== null) {
    const [, name, doubleQuotedValue, singleQuotedValue, unquotedValue] = match;
    attributes.set(name.toLowerCase(), doubleQuotedValue || singleQuotedValue || unquotedValue || '');
  }

  const src = attributes.get('src');
  if (!src) {
    return null;
  }

  const allowedSrc = normalizeAllowedIframeSrc(src);
  if (!allowedSrc) {
    return null;
  }

  return {
    src: allowedSrc,
    title: attributes.get('title') || 'Embedded video',
    allow: attributes.get('allow') || defaultIframeAllow,
  };
};

const parseBlogContent = (content: string): BlogContentPart[] => {
  const parts: BlogContentPart[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  iframePattern.lastIndex = 0;

  while ((match = iframePattern.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'markdown', value: content.slice(lastIndex, match.index) });
    }

    const iframe = parseIframeEmbed(match[0]);
    parts.push(iframe ? { type: 'iframe', value: iframe } : { type: 'markdown', value: match[0] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push({ type: 'markdown', value: content.slice(lastIndex) });
  }

  return parts;
};

export const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeKey = useRef(Date.now());

  useEffect(() => {
    const loadPost = async () => {
      try {
        if (!slug) {
          setError('No slug provided');
          return;
        }

        const postData = await blogService.getPostBySlug(slug);
        if (!postData) {
          setError('Post not found');
          return;
        }

        setPost(postData);
        document.title = `${postData.title} | Austin Davenport`;
        // Set canonical per post
        let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
        if (!link) {
          link = document.createElement('link');
          link.setAttribute('rel', 'canonical');
          document.head.appendChild(link);
        }
        const canonicalUrl = `${siteUrl}/blog/${postData.slug}`;
        link.setAttribute('href', canonicalUrl);
        const description = postData.excerpt || `Read ${postData.title} by Austin Davenport.`;
        setMeta('meta[name="description"]', 'content', description);
        setMeta('meta[property="og:url"]', 'content', canonicalUrl);
        setMeta('meta[property="og:title"]', 'content', `${postData.title} | Austin Davenport`);
        setMeta('meta[property="og:description"]', 'content', description);
        setMeta('meta[property="og:image"]', 'content', postData.image_url || `${siteUrl}/social-preview.png`);
        setMeta('meta[name="twitter:title"]', 'content', `${postData.title} | Austin Davenport`);
        setMeta('meta[name="twitter:description"]', 'content', description);
        setMeta('meta[name="twitter:image"]', 'content', postData.image_url || `${siteUrl}/social-preview.png`);
      } catch {
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    loadPost();

    return () => {
      document.title = 'Austin Davenport - Video Editor & Developer';
    };
  }, [slug]);

  const formatDate = (dateString: string) => {
    try {
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(new Date(dateString));
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <S.Container>
        <S.LoadingWrapper>Loading post...</S.LoadingWrapper>
      </S.Container>
    );
  }

  if (error || !post) {
    return (
      <S.Container>
        <S.ErrorWrapper>
          <div>{error || 'Post not found'}</div>
          <Button onClick={() => navigate('/blog')}>
            Back to Blog
          </Button>
        </S.ErrorWrapper>
      </S.Container>
    );
  }

  return (
    <S.Container>
      <S.BackButton onClick={() => navigate('/blog')}>
        <i className='bx bx-arrow-left' />
        Back to Blog
      </S.BackButton>

      <S.ArticleHeader>
        <S.Category>{post.category}</S.Category>
        <S.Title>{post.title}</S.Title>
        <S.Metadata>
          <span>{formatDate(post.published_at)}</span>
          <span>•</span>
          <span>{post.read_time} min read</span>
        </S.Metadata>
      </S.ArticleHeader>

      {post.youtube_video_id && (
        <S.VideoContainer>
          <S.VideoWrapper>
            <iframe
              src={`https://www.youtube.com/embed/${post.youtube_video_id}`}
              title="YouTube video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </S.VideoWrapper>
        </S.VideoContainer>
      )}

      {post.html_code && (
        <S.IframeContainer>
          <S.IframeWrapper>
            <iframe
              key={iframeKey.current}
              title={post.title}
              srcDoc={post.html_code}
              sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              style={{ border: 'none' }}
            />
          </S.IframeWrapper>
        </S.IframeContainer>
      )}

      {post.slug === 'building-my-own-vidiq' && (
        <S.HeroImage>
          <img src={buildingVidIqTopImageUrl} alt="Building My Own VidIQ" loading="eager" />
        </S.HeroImage>
      )}

      <S.ArticleContent>
        {parseBlogContent(post.content).map((part, index) => (
          part.type === 'markdown' ? (
            <ReactMarkdown key={`markdown-${index}`}>{part.value}</ReactMarkdown>
          ) : (
            <S.MarkdownIframeWrapper key={`iframe-${index}`}>
              <iframe
                src={part.value.src}
                title={part.value.title}
                allow={part.value.allow}
                allowFullScreen
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </S.MarkdownIframeWrapper>
          )
        ))}
      </S.ArticleContent>
    </S.Container>
  );
};

export default BlogPostPage;
